import type { EgkOcrFields } from "@omony/domain";
import { Button } from "@omony/ui";
import { Camera, CameraOff, ScanLine } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { api } from "../../shared/api.js";
import { getMedia, stopMedia } from "../../shared/media.js";
import { recognizeCard, warmUpOcr } from "../ocr/local.js";
import { useT } from "./common.js";
import styles from "./flow.module.css";

export type ScanResult = { fields: Partial<EgkOcrFields>; source: "vision" | "lokal_ocr" };

/** Seitenverhältnis einer ID-1-Karte (eGK) */
const CARD_RATIO = 85.6 / 53.98;
const FRAME_WIDTH = 0.82;

/** Ausschnitt im Kartenrahmen aus dem Videobild (object-fit: cover) in ein Canvas zeichnen */
function captureFrame(video: HTMLVideoElement): HTMLCanvasElement {
  const box = video.getBoundingClientRect();
  const vw = video.videoWidth;
  const vh = video.videoHeight;
  const scale = Math.max(box.width / vw, box.height / vh);
  const fw = (box.width * FRAME_WIDTH) / scale;
  const fh = fw / CARD_RATIO;
  const sx = (vw - fw) / 2;
  const sy = (vh - fh) / 2;
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1200, Math.round(fw));
  canvas.height = Math.round(canvas.width / CARD_RATIO);
  const ctx = canvas.getContext("2d")!;
  ctx.filter = "grayscale(1) contrast(1.3)";
  ctx.drawImage(video, sx, sy, fw, fh, 0, 0, canvas.width, canvas.height);
  return canvas;
}

export function CardScanner({ vision, onResult }: { vision: boolean; onResult: (r: ScanResult) => void }) {
  const t = useT();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [state, setState] = useState<"idle" | "starting" | "live" | "reading" | "error" | "denied">("idle");
  const [message, setMessage] = useState("");

  useEffect(() => () => stopMedia(stream), [stream]);
  useEffect(() => {
    if (videoRef.current && stream) videoRef.current.srcObject = stream;
  }, [stream]);

  const start = async () => {
    setState("starting");
    setMessage("");
    try {
      const s = await getMedia({
        video: { facingMode: { ideal: "environment" }, width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: false,
      });
      setStream(s);
      setState("live");
      warmUpOcr();
    } catch (e) {
      setState((e as DOMException).name === "NotAllowedError" ? "denied" : "error");
    }
  };

  const stop = () => {
    stopMedia(stream);
    setStream(null);
    setState("idle");
  };

  const scan = async () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    setState("reading");
    setMessage(t("console.scan.reading"));
    const canvas = captureFrame(video);
    let result: ScanResult | null = null;
    try {
      if (vision) {
        const r = await api<{ fields: EgkOcrFields }>("/ocr/egk", {
          method: "POST",
          body: { image: canvas.toDataURL("image/jpeg", 0.9) },
        }).catch(() => null);
        if (r) result = { fields: r.fields, source: "vision" };
      }
      if (!result) {
        const r = await recognizeCard(canvas);
        result = { fields: r.fields, source: "lokal_ocr" };
      }
    } catch {
      result = null;
    } finally {
      // Bild sofort verwerfen – es wird nie gespeichert
      canvas.width = 0;
      canvas.height = 0;
    }
    const count = result ? Object.values(result.fields).filter(Boolean).length : 0;
    if (!result || count === 0) {
      setState("live");
      setMessage(t("console.scan.nothing"));
      return;
    }
    onResult(result);
    setMessage(t("console.scan.found", { count }));
    stop();
  };

  return (
    <div className={styles.camera}>
      <div className={styles.viewport}>
        {stream ? (
          <>
            <video ref={videoRef} autoPlay playsInline muted />
            <div className={styles.cardFrame} aria-hidden="true" />
            <p className={styles.cameraHint}>{t("console.scan.hint")}</p>
          </>
        ) : (
          <div className={styles.cameraIdle}>
            <p>
              {state === "denied"
                ? t("console.scan.denied")
                : state === "error"
                  ? t("console.scan.error")
                  : t("console.scan.idle")}
            </p>
          </div>
        )}
      </div>
      <div className={styles.actions}>
        {stream ? (
          <>
            <Button variant="secondary" icon={<CameraOff />} onPress={stop}>
              {t("console.scan.stop")}
            </Button>
            <Button icon={<ScanLine />} onPress={() => void scan()} isDisabled={state === "reading"}>
              {t("console.scan.capture")}
            </Button>
          </>
        ) : (
          <Button
            variant="secondary"
            icon={<Camera />}
            onPress={() => void start()}
            isDisabled={state === "starting"}
          >
            {t("console.scan.start")}
          </Button>
        )}
      </div>
      <p className={styles.small} role="status" aria-live="polite">
        {message}
      </p>
      <p className={styles.small}>{t("console.scan.privacy")}</p>
    </div>
  );
}
